/**
 * Index de router
 * --------------------------------------------
 *
 * index con el objeto Router y el array de rutas
 *
 */
import Vue from "vue";
import VueRouter from "vue-router";
import Home from "../views/Home.vue";

import { isLoggedIn } from "@/utils/AuthService.js";

Vue.use(VueRouter);

/**
 * Routes
 * --------------------------------------------
 *
 * Array que contiene objetos con las rutas para cada vista.
 *
 * Cada objeto está formado por:
 *
 * path: la dirección de la ruta
 *
 * name: el nombre de la ruta
 *
 * component: el componente a mostrar en la ruta
 *
 * redirect(opcional): redirecciona a un componente dependiendo de si está logueado o no
 *
 * children: contiene un array con subrutas para cada vista
 *
 * -------------------------------------------
 *
 * En cada children hay una propiedad llamada meta que tiene un objeto con una propiedad llamada allowAnonymous.
 *
 * Dependiendo de sí es true o false permitirá ver o no la vista. De esto dependerác sí tiene permisos para ver si está logueado.
 *
 */
const routes = [
  {
    path: "/",
    name: "Home",
    component: Home,
    redirect: { name: isLoggedIn() ? "Map" : "Login" },
    children: [
      {
        path: "map",
        name: "Map",
        component: () => import("../views/Map.vue"),
        meta: {
          allowAnonymous: false,
        },
      },
      {
        path: "/dashboard",
        name: "Dashboard",
        component: () => import("../views/Dashboard.vue"),
        meta: {
          allowAnonymous: false,
        },
      },
      ,
      {
        path: "/reports",
        name: "Reports",
        component: () => import("../views/Reports.vue"),
        meta: {
          allowAnonymous: false,
        },
      },
      {
        path: "/reports/pdf",
        name: "GHtmltoPdfView",
        component: () => import("../views/GHtmltoPdfView.vue"),
        meta: {
          allowAnonymous: false,
        },
        props: true,
      },
      {
        path: "/report/:id/:id2",
        name: "HtmlToPdfView",
        component: () => import("../views/HtmlToPdfView.vue"),
        meta: {
          allowAnonymous: true,
        },
        props: true,
      },
      {
        path: "/alarms",
        name: "Alarms",
        component: () => import("../views/Alarms.vue"),
        meta: {
          allowAnonymous: false,
        },
      },
      {
        path: "/configuration",
        name: "Configuration",
        component: () => import("../views/Configuration.vue"),
        meta: {
          allowAnonymous: false,
        },
      },
      {
        path: "/profile",
        name: "Profile",
        component: () => import("../views/Profile.vue"),
        meta: {
          allowAnonymous: false,
        },
      },
    ],
  },
  {
    path: "/login",
    name: "Login",
    component: () => import("../views/auth/Login.vue"),
    meta: {
      allowAnonymous: true,
    },
  },
];

/**
 * Objeto VueRouter
 * --------------------------------------------
 *
 * Creación del objeto router con las propiedades mode, base, y el array de routes
 *
 */
const router = new VueRouter({
  mode: "history",
  base: process.env.BASE_URL,
  routes,
});

/**
 * Bucle que recorre cada vez que entra en una vista comprobando si puede o no entrar a partir de si está loguedo y sí se puede entrar sin registro
 * --------------------------------------------
 * Redirige al login si no cumple los requisitos de la condición.
 */
router.beforeEach((to, from, next) => {
  if (!to.meta.allowAnonymous && !isLoggedIn()) {
    next({
      path: "/login",
      query: { redirect: to.fullPath },
    });
  } else {
    next();
  }
});

export default router;
